/**
 * WalletManager - Client-side wallet integration for Crimson Mandate.
 *
 * Supports:
 *   - Solana: native Phantom/Solflare
 *   - EVM (Ethereum/Polygon/BSC): MetaMask ERC-20 transfers
 *   - GalaChain: Gala Wallet via WPM (Web Post Message) direct content-script channel
 *
 * GalaChain notes:
 *   The Gala Wallet content script runs on ALL pages (manifest <all_urls>).
 *   The inpage bundle (window.gala provider) is also injected on crimsonmandate.com,
 *   so window.gala.address is already set without any popup. Signing uses
 *   window.gala.request({ method: 'eth_signTypedData_v4' }) when available.
 *   WPM channel is kept as a fallback for domains where inpage is not injected.
 *   Wire format (@metamask/post-message-stream v9):
 *     Send:    window.postMessage({ target: "gala-contentscript", data: chunk })
 *     Receive: { target: "gala-inpage", data: chunk }
 *   Handshake: page sends SYN → CS responds ACK → ready
 *
 * Registered as a singleton via ServiceLocator in BootScene.
 */

import { TypedDataEncoder } from 'ethers';
import { EventBus } from '../utils/EventBus';
import { canonicalStringify } from '../utils/canonicalJson';
import { WalletEvents } from './WalletEventTypes';

// ============ Solana Configuration ============
// Solflare/MetaMask Solana Snap support was removed as deprecated/unreliable.
// Native Phantom/Solflare remain the supported Solana path.

// ============ Inline Base58 Codec ============

const BASE58_ALPHABET = '123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz';

function base58Encode(bytes: Uint8Array): string {
  // Count leading zeros
  let leadingZeros = 0;
  for (let i = 0; i < bytes.length && bytes[i] === 0; i++) {
    leadingZeros++;
  }

  // Convert to base58
  // Use a simple big-number approach
  let num = 0n;
  for (const b of bytes) {
    num = num * 256n + BigInt(b);
  }

  let result = '';
  while (num > 0n) {
    const mod = Number(num % 58n);
    result = BASE58_ALPHABET[mod] + result;
    num = num / 58n;
  }

  // Prepend '1' for each leading zero byte
  return '1'.repeat(leadingZeros) + result;
}

function base58Decode(str: string): Uint8Array {
  // Count leading '1's
  let leadingOnes = 0;
  for (let i = 0; i < str.length && str[i] === '1'; i++) {
    leadingOnes++;
  }

  // Convert from base58
  let num = 0n;
  for (const c of str) {
    const idx = BASE58_ALPHABET.indexOf(c);
    if (idx < 0) throw new Error(`Invalid base58 character: ${c}`);
    num = num * 58n + BigInt(idx);
  }

  // Convert bigint to bytes
  const hex = num === 0n ? '' : num.toString(16);
  const paddedHex = hex.length % 2 ? '0' + hex : hex;
  const dataBytes = new Uint8Array(paddedHex.length / 2);
  for (let i = 0; i < dataBytes.length; i++) {
    dataBytes[i] = parseInt(paddedHex.slice(i * 2, i * 2 + 2), 16);
  }

  // Prepend zero bytes for leading '1's
  const result = new Uint8Array(leadingOnes + dataBytes.length);
  result.set(dataBytes, leadingOnes);
  return result;
}

// ============ Types ============

export interface WalletInfo {
  address: string;   // base58 Solana address or Gala Wallet address (client|...)
  chain: 'solana' | 'galachain';
  walletName: string;
  isConnected: boolean;
}

export interface SolanaSignResult {
  publicKey: string;   // base58
  signature: string;   // base58
}

/**
 * MetaMask EIP-1193 provider interface.
 */
interface EIP1193Provider {
  isMetaMask?: boolean;
  isGalaWallet?: boolean;
  isCoinbaseWallet?: boolean;
  request(args: { method: string; params?: unknown[] | Record<string, unknown> }): Promise<unknown>;
  on(event: string, callback: (...args: any[]) => void): void;
  removeListener(event: string, callback: (...args: any[]) => void): void;
  providers?: EIP1193Provider[];
}

declare global {
  interface Window {
    ethereum?: EIP1193Provider;
  }
}

// ============ WalletManager ============

export class WalletManager {
  private connectedWallet: WalletInfo | null = null;
  private provider: EIP1193Provider | null = null;
  private nativeSolanaProvider: any = null;

  /** Auto-report client-side errors to the bug API */
  private reportClientError(title: string, observed: string): void {
    try {
      const token = localStorage.getItem('crimsonmandate_token');
      if (!token) return;
      fetch('/api/bugs/report', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          title: `[Client] ${title}`.slice(0, 200),
          category: 'other',
          severity: 'major',
          observed,
          logSnippet: JSON.stringify({ userAgent: navigator.userAgent, url: window.location.href, time: new Date().toISOString() }),
        }),
      }).catch(() => {}); // fire and forget
    } catch { /* ignore */ }
  }

  // ── WPM (Gala Wallet direct postMessage channel) ──
  private wpmStreamReady = false;
  private wpmStreamInitializing = false;
  private wpmHaveSyn = false;
  private wpmPendingRequests = new Map<string, {
    resolve: (value: any) => void;
    reject: (reason: any) => void;
    timeout: ReturnType<typeof setTimeout>;
  }>();

  constructor() {
    console.log('[WalletManager] Initialized.');
    this.initWpmListener();
  }

  // ============ WPM Channel (Gala Wallet direct postMessage) ============

  /**
   * Set up the persistent message listener for the Gala Wallet WPM channel.
   * Called once in the constructor.
   */
  private initWpmListener(): void {
    if (typeof window === 'undefined') return;

    window.addEventListener('message', (event) => {
      if (event.source !== window) return;
      const msg = event.data as any;
      if (!msg || typeof msg !== 'object') return;
      // Log ALL gala-related messages for debugging
      if (msg.target === 'gala-inpage' || msg.target === 'gala-contentscript' || (msg.data && typeof msg.data === 'object' && msg.data.id)) {
        console.log('[WalletManager] WPM raw:', JSON.stringify({ target: msg.target, data: typeof msg.data === 'string' ? msg.data : '(object)' }).slice(0, 200));
      }

      if (msg.target !== 'gala-inpage') return;

      const data = msg.data;

      // ── Handshake messages ──
      if (data === 'SYN') {
        // CS is (re-)initializing the stream — reset ready state so subsequent
        // galaWpmRequest calls wait for the new handshake to complete before
        // sending data.  This prevents signTypedMessage from being dropped when
        // the CS re-inits immediately after the requestAccounts popup closes.
        this.wpmStreamReady = false;
        this.wpmStreamInitializing = true;
        this.wpmHaveSyn = true;
        window.postMessage({ target: 'gala-contentscript', data: 'ACK' }, window.location.origin);
        return;
      }
      if (data === 'ACK') {
        if (!this.wpmStreamReady) {
          if (!this.wpmHaveSyn) {
            // Got ACK before SYN — complete the handshake
            window.postMessage({ target: 'gala-contentscript', data: 'ACK' }, window.location.origin);
          }
          this.wpmStreamReady = true;
          this.wpmStreamInitializing = false;
          this.wpmHaveSyn = false; // reset for next round
          console.log('[WalletManager] Gala Wallet WPM channel ready.');
        }
        return;
      }

      // ── Data responses ──
      if (data && typeof data === 'object') {
        console.log('[WalletManager] WPM← data:', JSON.stringify(data).slice(0, 300));

        // user_rejected broadcast fires after ANY popup closes (even approved ones).
        // Ignore it — real rejections come as error responses on specific request IDs.
        if (data.method === 'user_rejected' || data.error === 'user_rejected') {
          console.log('[WalletManager] WPM user_rejected broadcast (spurious — ignoring)');
          return;
        }

        const pending = data.id ? this.wpmPendingRequests.get(data.id) : null;
        if (pending) {
          clearTimeout(pending.timeout);
          this.wpmPendingRequests.delete(data.id);
          if (data.error) {
            pending.reject(new Error(typeof data.error === 'string' ? data.error : JSON.stringify(data.error)));
          } else {
            pending.resolve(data.result);
          }
        }
      }
    });
  }

  /**
   * Ensure the WPM SYN/ACK handshake with the Gala Wallet content script is complete.
   */
  private ensureWpmHandshake(): Promise<void> {
    if (this.wpmStreamReady) return Promise.resolve();

    if (this.wpmStreamInitializing) {
      return new Promise((resolve, reject) => {
        const check = setInterval(() => {
          if (this.wpmStreamReady) { clearInterval(check); resolve(); }
        }, 50);
        setTimeout(() => {
          clearInterval(check);
          if (!this.wpmStreamReady) reject(new Error('Gala Wallet handshake timed out'));
        }, 6000);
      });
    }

    this.wpmStreamInitializing = true;
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.wpmStreamInitializing = false;
        reject(new Error('Gala Wallet extension not detected. Install the Gala Wallet Chrome extension to use GalaChain payments.'));
      }, 6000);

      const check = setInterval(() => {
        if (this.wpmStreamReady) { clearInterval(check); clearTimeout(timer); resolve(); }
      }, 50);

      console.log('[WalletManager] Initiating Gala Wallet WPM handshake…');
      window.postMessage({ target: 'gala-contentscript', data: 'SYN' }, window.location.origin);
    });
  }

  /**
   * Send a request to the Gala Wallet content script via WPM and await the response.
   *
   * @param name - Method name (e.g., 'requestAccounts', 'signTypedMessage')
   * @param object - Request payload
   * @param timeoutMs - How long to wait for a response (default 60s)
   */
  private galaWpmRequest(name: string, object: Record<string, unknown>, timeoutMs = 60000): Promise<any> {
    return this.ensureWpmHandshake().then(() => {
      return new Promise((resolve, reject) => {
        const id = Date.now().toString(36) + Math.random().toString(36).slice(2);

        const timeout = setTimeout(() => {
          this.wpmPendingRequests.delete(id);
          reject(new Error(`Gala Wallet request "${name}" timed out after ${timeoutMs / 1000}s`));
        }, timeoutMs);

        this.wpmPendingRequests.set(id, { resolve, reject, timeout });

        const chunk = {
          id,
          name,
          object: object || {},
          network: 'ethereum',
          title: document.title,
          url: window.location.origin,
          image: null,
        };

        console.log(`[WalletManager] WPM→ ${name} id=${id.slice(0, 8)}`);
        window.postMessage({ target: 'gala-contentscript', data: chunk }, window.location.origin);
      });
    });
  }

  // ============ Connection ============

  /**
   * Connect to Solana via native Phantom or Solflare.
   */
  async connectSolana(): Promise<WalletInfo> {
    const nativeSolana = this.getNativeSolanaProvider();
    if (nativeSolana) {
      console.log('[WalletManager] Native Solana wallet detected, using it directly.');
      return this.connectNativeSolana();
    }

    console.log('[WalletManager] No native Solana wallet found.');

    throw new Error(
      'No Solana wallet found. Please install or open Phantom (phantom.app) or Solflare (solflare.com).',
    );
  }

  /**
   * Sign and broadcast a Solana transaction via the connected native wallet.
   *
   * @param messageBase58 - The serialized Solana transaction message in base58
   * @returns { publicKey, signature } both in base58
   */
  async signSolanaTransaction(messageBase58: string): Promise<SolanaSignResult> {
    this.requireConnected();

    // If connected via native Solana wallet, use native signing
    if (this.nativeSolanaProvider) {
      return this.signNativeSolanaTransaction(messageBase58);
    }

    // No native Solana provider — cannot sign
    throw new Error('No Solana wallet connected. Please connect Phantom or Solflare.');
  }

  /**
   * Disconnect the currently connected wallet.
   */
  async disconnect(): Promise<void> {
    console.log('[WalletManager] Disconnecting wallet...');
    if (this.nativeSolanaProvider) {
      try { await this.nativeSolanaProvider.disconnect(); } catch (_) { /* ignore */ }
    }
    this.provider = null;
    this.nativeSolanaProvider = null;
    this.connectedWallet = null;
    EventBus.emit(WalletEvents.DISCONNECTED);
    console.log('[WalletManager] Wallet disconnected.');
  }

  // ============ State ============

  isConnected(): boolean {
    return this.connectedWallet !== null && this.connectedWallet.isConnected;
  }

  getWalletInfo(): WalletInfo | null {
    return this.connectedWallet ? { ...this.connectedWallet } : null;
  }

  getSolanaAddress(): string | null {
    return this.connectedWallet?.address ?? null;
  }

  isMetaMaskInstalled(): boolean {
    return this.getProvider() !== null;
  }

  /**
   * Check if the Gala Wallet browser extension is installed by testing the WPM channel.
   * This is a lightweight probe — handshake completes in ~100ms if installed.
   */
  isGalaWalletInstalled(): boolean {
    // Optimistically return true if handshake already succeeded
    return this.wpmStreamReady;
  }

  // ============ Base58 Utilities (exposed for tests) ============

  static base58Encode = base58Encode;
  static base58Decode = base58Decode;

  // ============ Provider Detection ============

  private getProvider(): EIP1193Provider | null {
    if (typeof window === 'undefined') return null;

    const ethereum = window.ethereum as any;
    if (!ethereum) return null;

    const isSolanaImpersonator = (p: any) => Boolean(p?.isPhantom || p?.isSolflare);

    // Helper: true MetaMask provider (not Solana wallets impersonating window.ethereum)
    const isRealMetaMask = (p: any) =>
      p.isMetaMask && !p.isCoinbaseWallet && !isSolanaImpersonator(p);

    const isUsableEvmProvider = (p: any) =>
      p && typeof p.request === 'function' && !isSolanaImpersonator(p);

    // When multiple wallet extensions are installed, check providers array first
    if (Array.isArray(ethereum.providers)) {
      // Prefer real MetaMask (exclude Phantom/Solflare EVM impersonators)
      const metaMask = ethereum.providers.find(isRealMetaMask);
      if (metaMask) return metaMask;

      // Fall back to any real EVM provider. Do not use Solana wallets for EVM payments.
      const evmProvider = ethereum.providers.find(isUsableEvmProvider);
      if (evmProvider) return evmProvider;
    }

    // Direct check - MetaMask is the only wallet (not a Solana wallet)
    if (isRealMetaMask(ethereum)) return ethereum;

    // window.ethereum exists but isn't MetaMask — use it only if it is a real EVM provider.
    if (isUsableEvmProvider(ethereum)) return ethereum;

    return null;
  }

  // ============ Native Solana Wallet Detection ============

  /**
   * Detect a native Solana wallet extension (Phantom or Solflare).
   * Returns the provider or null if no native Solana wallet is found.
   */
  private getNativeSolanaProvider(): any | null {
    if (typeof window === 'undefined') return null;

    const win = window as any;

    // Phantom
    if (win.phantom?.solana?.isPhantom) {
      return win.phantom.solana;
    }

    // Solflare
    if (win.solflare?.isSolflare) {
      return win.solflare;
    }

    return null;
  }

  /**
   * Connect to a native Solana wallet (Phantom or Solflare).
   */
  async connectNativeSolana(): Promise<WalletInfo> {
    const solProvider = this.getNativeSolanaProvider();
    if (!solProvider) {
      throw new Error('No native Solana wallet found.');
    }

    const walletName = solProvider.isPhantom ? 'Phantom' : 'Solflare';
    console.log(`[WalletManager] Connecting to ${walletName}...`);

    try {
      const resp = await solProvider.connect();
      // Solflare sometimes resolves connect() just before publicKey is populated.
      let pubKey = resp?.publicKey || solProvider.publicKey;
      for (let attempt = 0; !pubKey && attempt < 30; attempt++) {
        await new Promise(r => setTimeout(r, 50));
        pubKey = solProvider.publicKey;
      }
      if (!pubKey) throw new Error(`${walletName} connected but no public key returned`);
      const address = pubKey.toString();

      this.provider = null; // native Solana wallets don't use EIP-1193
      this.nativeSolanaProvider = solProvider;
      this.connectedWallet = {
        address,
        chain: 'solana',
        walletName,
        isConnected: true,
      };

      console.log(`[WalletManager] Connected ${walletName}: ${address}`);
      EventBus.emit(WalletEvents.CONNECTED, { ...this.connectedWallet });

      return { ...this.connectedWallet };
    } catch (error: any) {
      const message = error?.message || 'Unknown error';
      console.error(`[WalletManager] Failed to connect ${walletName}:`, message);

      if (error?.code === 4001) {
        throw new Error('Wallet connection was rejected by the user.');
      }

      throw new Error(`Failed to connect ${walletName}: ${message}`);
    }
  }

  /**
   * Sign a Solana transaction via a native wallet (Phantom or Solflare).
   *
   * @param messageBase58 - The serialized Solana transaction message in base58
   * @returns { publicKey, signature } both in base58
   */
  async signNativeSolanaTransaction(messageBase58: string): Promise<SolanaSignResult> {
    if (!this.nativeSolanaProvider) {
      throw new Error('No native Solana wallet connected.');
    }

    try {
      console.log('[WalletManager] Requesting native Solana transaction signature...');

      // Server sends serialized message bytes (base58-encoded).
      // Build wire-format transaction: [1 (num sigs), 64 zero bytes (placeholder), message]
      const messageBytes = base58Decode(messageBase58);
      const txBytes = new Uint8Array(1 + 64 + messageBytes.length);
      txBytes[0] = 1;
      txBytes.set(messageBytes, 65);

      const publicKey = this.connectedWallet!.address;

      // Use signAndSendTransaction — Phantom signs AND broadcasts to Solana network.
      // Server will verify the signature on-chain instead of re-submitting.
      console.log('[WalletManager] Using signAndSendTransaction...');
      const result = await this.nativeSolanaProvider.signAndSendTransaction(
        { serialize: () => txBytes },
      );
      // Phantom returns { signature: "base58_tx_signature", publicKey: "..." }
      const signature = result.signature || result;
      console.log('[WalletManager] Transaction broadcast by wallet. Signature:', String(signature).slice(0, 20) + '...');

      console.log('[WalletManager] Native Solana transaction signed. Signature:', signature.slice(0, 20) + '...');
      EventBus.emit(WalletEvents.TRANSACTION_SENT, { signature });

      return { publicKey, signature };
    } catch (error: any) {
      // Extract the best available error message from nested wallet error shapes.
      // Phantom/Solflare often bury the real reason in error.error.message, error.data.message, or error.cause.
      const extractMsg = (e: any): string => {
        if (!e) return 'Transaction signing failed';
        return (
          e.message ||
          e.error?.message ||
          e.data?.message ||
          e.cause?.message ||
          (typeof e === 'string' ? e : 'Transaction signing failed')
        );
      };
      const message = extractMsg(error);
      const code = error?.code ?? error?.error?.code;
      const isUserRejection = code === 4001 || /user rejected|user denied/i.test(message);

      console.error('[WalletManager] signNativeSolanaTransaction failed:', { message, code, error });
      EventBus.emit(WalletEvents.TRANSACTION_FAILED, { error: message });

      // Only auto-report real failures to the bug API, never user rejections (4001).
      if (!isUserRejection) {
        this.reportClientError('Solana transaction signing failed', message);
      }

      if (isUserRejection) {
        throw new Error('Transaction was rejected by the user.');
      }

      throw new Error(`Native Solana transaction signing failed: ${message}`);
    }
  }

  // ============ Guards ============

  private requireConnected(): void {
    if (!this.isConnected()) {
      throw new Error('No wallet connected. Please connect a wallet first.');
    }
  }

  /** @internal Used by EVM paths */
  public requireProvider(): void {
    if (!this.provider) throw new Error('Wallet provider not available. Please reconnect your wallet.');
  }

  // ============ Multi-Chain Connection ============

  /**
   * Connect the wallet for a given chain.
   * - solana → connectSolana() (native Phantom/Solflare)
   * - ethereum/polygon/bsc → getEVMAddress() + switch chain
   * - galachain → connectGalaChain() (Gala Wallet via WPM)
   */
  async connectForChain(chain: string): Promise<void> {
    if (chain === 'solana') {
      if (!this.isConnected() || this.connectedWallet?.chain !== 'solana') {
        await this.connectSolana();
      }
    } else if (chain === 'galachain') {
      if (!this.isConnected() || this.connectedWallet?.chain !== 'galachain') {
        await this.connectGalaChain();
      }
    } else {
      // EVM chains (ethereum, polygon, bsc)
      const provider = this.getProvider();
      if (!provider) {
        // No EVM wallet available — check for native Solana wallet as fallback
        const nativeSolana = this.getNativeSolanaProvider();
        if (nativeSolana) {
          throw new Error('NO_EVM_WALLET_SOLANA_AVAILABLE');
        }
        window.open('https://metamask.io/download/', '_blank');
        throw new Error('No EVM wallet found. Please install MetaMask or Coinbase Wallet.');
      }

      await this.getEVMAddress();
      const chainIds: Record<string, string> = {
        ethereum: '0x1',
        polygon: '0x89',
        bsc: '0x38',
      };
      const targetChainId = chainIds[chain];
      if (targetChainId) {
        const currentChainId = await provider.request({ method: 'eth_chainId' }) as string;
        if (currentChainId.toLowerCase() !== targetChainId.toLowerCase()) {
          try {
            await provider.request({
              method: 'wallet_switchEthereumChain',
              params: [{ chainId: targetChainId }],
            });
          } catch (err: any) {
            throw new Error(`Please switch your wallet to the ${chain} network.`);
          }
        }
      }
    }
  }

  // ============ EVM (Ethereum/Polygon/BSC) ============

  /**
   * Get the connected MetaMask EVM address (Ethereum, Polygon, BSC).
   * Prompts MetaMask to connect if not already connected.
   */
  async getEVMAddress(): Promise<string> {
    const provider = this.getProvider();
    if (!provider) {
      window.open('https://metamask.io/download/', '_blank');
      throw new Error('No EVM wallet found. Please install MetaMask or Coinbase Wallet.');
    }

    const accounts = await provider.request({ method: 'eth_requestAccounts' }) as string[];
    if (!accounts || accounts.length === 0) {
      throw new Error('No EVM accounts available. Please unlock your wallet.');
    }

    return accounts[0];
  }

  /**
   * Send an ERC-20 token transfer via MetaMask.
   * Builds the transfer(address,uint256) calldata and sends via eth_sendTransaction.
   *
   * @param tokenAddress - ERC-20 contract address
   * @param toAddress - recipient (treasury)
   * @param amountRaw - amount in token's smallest unit (hex string, e.g. "0x4C4B40" for 5 USDC)
   * @param chainId - hex chain ID (e.g. "0x1" for Ethereum mainnet)
   * @returns transaction hash
   */
  async sendERC20Transfer(
    tokenAddress: string,
    toAddress: string,
    amountRaw: string,
    chainId: string,
  ): Promise<string> {
    const provider = this.getProvider();
    if (!provider) {
      window.open('https://metamask.io/download/', '_blank');
      throw new Error('No EVM wallet found. Please install MetaMask or Coinbase Wallet.');
    }

    // Ensure correct chain
    const currentChainId = await provider.request({ method: 'eth_chainId' }) as string;
    if (currentChainId.toLowerCase() !== chainId.toLowerCase()) {
      try {
        await provider.request({
          method: 'wallet_switchEthereumChain',
          params: [{ chainId }],
        });
      } catch (switchErr: any) {
        throw new Error(`Please switch MetaMask to the correct network (chain ${chainId}).`);
      }
    }

    const fromAddress = await this.getEVMAddress();

    // ERC-20 transfer(address,uint256) function selector = 0xa9059cbb
    const toAddrPadded = toAddress.slice(2).toLowerCase().padStart(64, '0');
    const amountHex = amountRaw.startsWith('0x') ? amountRaw.slice(2) : BigInt(amountRaw).toString(16);
    const amountPadded = amountHex.padStart(64, '0');
    const data = '0xa9059cbb' + toAddrPadded + amountPadded;

    console.log(`[WalletManager] Sending ERC-20 transfer: token=${tokenAddress}, to=${toAddress}, amount=${amountRaw}`);

    const txHash = await provider.request({
      method: 'eth_sendTransaction',
      params: [{
        from: fromAddress,
        to: tokenAddress,
        data,
      }],
    }) as string;

    console.log(`[WalletManager] EVM transaction sent: ${txHash}`);
    EventBus.emit(WalletEvents.TRANSACTION_SENT, { txHash, chain: 'ethereum' });

    return txHash;
  }

  // ============ GalaChain / Gala Wallet (WPM channel) ============

  /**
   * Connect to the Gala Wallet and get the user's GalaChain address.
   *
   * Priority:
   *   1. window.gala.address — inpage bundle sets this without any popup on crimsonmandate.com
   *   2. WPM requestAccounts — fallback for domains where inpage is not injected
   */
  async connectGalaChain(): Promise<WalletInfo> {
    // If already connected to GalaChain, return existing connection
    if (this.connectedWallet?.isConnected && this.connectedWallet.chain === 'galachain') {
      console.log('[WalletManager] Already connected to GalaChain:', this.connectedWallet.address);
      return { ...this.connectedWallet };
    }

    // The Gala Wallet inpage bundle injects window.gala on crimsonmandate.com.
    // If address is already set, no popup is needed — use it directly.
    const galaInpage = (window as any).gala;
    if (galaInpage?.address) {
      const address = galaInpage.address as string;
      console.log(`[WalletManager] Gala Wallet already authorized via window.gala: ${address}`);
      this.connectedWallet = {
        address,
        chain: 'galachain',
        walletName: 'Gala Wallet',
        isConnected: true,
      };
      EventBus.emit(WalletEvents.CONNECTED, { ...this.connectedWallet });
      return { ...this.connectedWallet };
    }

    // Check if Gala Wallet extension is installed (WPM channel available)
    // The WPM channel is injected by the Gala Wallet extension as a BroadcastChannel
    const hasWpm = typeof BroadcastChannel !== 'undefined';
    const hasGalaProvider = !!(window as any).gala;

    if (!hasGalaProvider && !hasWpm) {
      // No Gala Wallet detected — prompt user to install it
      console.log('[WalletManager] Gala Wallet not detected, prompting install…');
      window.open('https://chromewebstore.google.com/detail/gala-games-wallet/noclhpfcbjkcpmakeejhdeomhfmemjfn', '_blank');
      throw new Error('Gala Wallet not detected. Please install the Gala Wallet browser extension and reload the page.');
    }

    // Fallback: use WPM requestAccounts (triggers connect popup)
    console.log('[WalletManager] window.gala not available, connecting via WPM channel…');
    try {
      // Race: WPM response OR poll for window.gala being set after popup approval
      const wpmPromise = this.galaWpmRequest('requestAccounts', {}, 120000) as Promise<string[] | null>;
      const pollPromise = new Promise<string[] | null>((resolve) => {
        let attempts = 0;
        const poll = setInterval(() => {
          attempts++;
          const gala = (window as any).gala;
          if (gala?.address) {
            clearInterval(poll);
            console.log(`[WalletManager] window.gala appeared after ${attempts * 500}ms: ${gala.address}`);
            resolve([gala.address]);
          }
          if (attempts > 240) { // 2 minutes
            clearInterval(poll);
            resolve(null);
          }
        }, 500);
      });
      const accounts = await Promise.race([wpmPromise, pollPromise]);

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts returned from Gala Wallet. Make sure the extension is unlocked and try again.');
      }

      const address = accounts[0];
      console.log(`[WalletManager] Gala Wallet address (WPM): ${address}`);

      this.connectedWallet = {
        address,
        chain: 'galachain',
        walletName: 'Gala Wallet',
        isConnected: true,
      };

      EventBus.emit(WalletEvents.CONNECTED, { ...this.connectedWallet });
      return { ...this.connectedWallet };
    } catch (error: any) {
      const message = error?.message || 'Unknown error';
      console.error('[WalletManager] Failed to connect Gala Wallet:', message);
      throw new Error(`Failed to connect Gala Wallet: ${message}`);
    }
  }

  /**
   * Sign a pre-built EIP-712 typed data object from the server via the Gala Wallet WPM channel.
   *
   * The server provides the exact types, domain, and message. We:
   *   1. Compute the EIP-712 hash client-side (ethers TypedDataEncoder)
   *   2. Send the hash + full typed data JSON to Gala Wallet's signTypedMessage method
   *
   * This matches the working testthewallet.html approach.
   */
  async signGalaChainTypedData(typedData: {
    types: Record<string, Array<{ name: string; type: string }>>;
    primaryType: string;
    domain: Record<string, unknown>;
    message: Record<string, unknown>;
  }): Promise<string> {
    const address = this.connectedWallet?.address;
    if (!address) {
      throw new Error('Gala Wallet not connected. Call connectGalaChain() first.');
    }
    if (this.connectedWallet?.chain !== 'galachain') {
      throw new Error(`signGalaChainTypedData requires a GalaChain wallet, but connected wallet is on chain "${this.connectedWallet?.chain}". Call connectGalaChain() first.`);
    }

    // Strip EIP712Domain — TypedDataEncoder handles domain separately
    const { EIP712Domain: _ignored, ...signingTypes } = typedData.types;

    // Compute EIP-712 hash client-side (needed for WPM path)
    const hashHex = TypedDataEncoder.hash(typedData.domain as any, signingTypes, typedData.message);
    console.log(`[WalletManager] EIP-712 hash: ${hashHex.slice(0, 20)}…`);

    // ── Path 1: window.gala EIP-1193 request (preferred — no WPM race condition) ──
    const galaInpage = (window as any).gala;
    if (galaInpage?.request || galaInpage?.rpc) {
      console.log('[WalletManager] Signing via window.gala EIP-1193 path…');
      try {
        const requestFn = galaInpage.request?.bind(galaInpage) ?? galaInpage.rpc?.bind(galaInpage);
        const signature = await requestFn({
          method: 'eth_signTypedData_v4',
          params: [address, canonicalStringify(typedData)],
        }) as string;

        if (!signature) throw new Error('Empty signature from window.gala');

        console.log('[WalletManager] GalaChain typed data signed via window.gala.');
        EventBus.emit(WalletEvents.TRANSACTION_SENT, { signature });
        return signature;
      } catch (err: any) {
        const msg = err?.message || String(err);
        // User explicitly rejected — don't fall through
        if (msg.includes('rejected') || msg.includes('denied') || err?.code === 4001) {
          EventBus.emit(WalletEvents.TRANSACTION_FAILED, { error: msg });
          throw new Error(`GalaChain signing rejected: ${msg}`);
        }
        console.warn('[WalletManager] window.gala EIP-1193 signing failed, falling back to WPM:', msg);
      }
    }

    // ── Path 2: WPM direct channel (fallback) ──
    try {
      const rawStr = canonicalStringify(typedData);
      console.log('[WalletManager] Sending signTypedMessage via WPM… (Gala Wallet popup will appear)');
      const signature = await this.galaWpmRequest('signTypedMessage', {
        data: hashHex,
        raw: rawStr,
        address,
        version: 'V4',
      }, 120000) as string;

      if (!signature) {
        throw new Error('Empty signature returned from Gala Wallet.');
      }

      console.log('[WalletManager] GalaChain typed data signed via WPM.');
      EventBus.emit(WalletEvents.TRANSACTION_SENT, { signature });
      return signature;
    } catch (error: any) {
      const message = error?.message || 'Signing failed';
      console.error('[WalletManager] signGalaChainTypedData failed:', message);
      EventBus.emit(WalletEvents.TRANSACTION_FAILED, { error: message });
      throw new Error(`GalaChain signing failed: ${message}`);
    }
  }

  // ============ Lifecycle ============

  destroy(): void {
    console.log('[WalletManager] Destroying...');
    this.provider = null;
    this.nativeSolanaProvider = null;
    this.connectedWallet = null;
    // Clear any pending WPM requests
    for (const [, pending] of this.wpmPendingRequests) {
      clearTimeout(pending.timeout);
      pending.reject(new Error('WalletManager destroyed'));
    }
    this.wpmPendingRequests.clear();
  }
}
